import AddButton from "@components/AddButton/AddButton";
import EmptyList from "@components/EmptyList/EmptyList";
import Header from "@components/Header/Header";
import SectionLabel from "@components/SectionLabel/SectionLabel";
import AccountListItem from "@components/AccountListItem/AccountListItem";
import { PALE_BLUE } from "@styles/colors";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import {
    Container,
    BodyContainer,
    ViewModeContainer,
    EmptyListContainer,
    EmptyListText,
    EWalletItemContainer,
    BottomButtonContainer,
} from "./ManageEWallet.styles";
import { EWalletForm } from "./Form";
import useEWalletManagement from "@hooks/useManageEWallet";
import LoadingModal from "@components/LoadingModal/LoadingModal";
import type { EWallet } from "@hooks/useManageEWallet";
import { ModalWithIcon } from "@components/ModalWithIcon/ModalWithIcon";
import SuccessModal from '@components/SuccessModal/SuccessModal';
import { ApiError } from "@lib/api";
import Snackbar from "@components/Snackbar/Snackbar";
import DialogConfirmationBox from "@components/DialogConfirmationBox/DialogConfirmationBox";
import { useUserRestrictions } from "@hooks/useUserRestrictions";

const ManageEWallet = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const {
        ewallets,
        isLoading: isLoadingEWallets,
        supportedEWallets,
        addEWallet,
        editEWallet,
        removeEWallet,
        validateEWallet,
        reload,
    } = useEWalletManagement()

    const { canManageEwalletAccounts } = useUserRestrictions()

    const [successOpen, setSuccessOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [verificationModalVisible, setVerificationModalVisible] = useState(false)
    const [failedModalVisible, setFailedModalVisible] = useState(false)
    const [isError, setIsError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('');
    const [isLocalLoading, setIsLocalLoading] = useState(false)
    const [isConfirmationDialogVisible, setIsConfirmationDialogVisible] = useState(false)
    const [toBeRemoved, setToBeRemoved] = useState('')

    // Check for mode and source in URL query params
    const queryParams = new URLSearchParams(location.search);
    const rawMode = queryParams.get('mode');
    const initialMode = rawMode === 'view' || rawMode === 'edit' || rawMode === 'add' ? rawMode : 'view';
    const source = queryParams.get('source');

    const [mode, setMode] = useState<'view' | 'edit' | 'add'>(initialMode);
    const [selectedEWallet, setSelectedEWallet] = useState<EWallet | null>(null);

    const currentAmount = queryParams.get('amount')
    const parsedAmount = currentAmount ? Number(currentAmount) : undefined
    const validAmount = parsedAmount && !isNaN(parsedAmount) && parsedAmount > 0 ? parsedAmount : undefined

    const onBackClick = async (uuid?: string) => {
        if (source === 'redeem-e-wallet') {
            navigate('/redeem-ewallet', {
                state: {
                    ewallet_uuid: uuid,
                    amount: validAmount
                }
            });
            return;
        }
        if (mode === 'edit' || mode === 'add') {
            await reload()
            setMode('view');
            setSelectedEWallet(null);
            setIsError(false)
            setErrorMessage('')
        } else {
            navigate('/bank-and-address')
        }
    };

    const handleEWalletClick = (ewallet: EWallet) => {
        setSelectedEWallet(ewallet);
        setMode('edit');
    };

    const handleAddNewClick = () => {
        setSelectedEWallet(null);
        setMode('add');
    };

    const handlePromptConfirmation = async (uuid: string) => {
        setIsConfirmationDialogVisible(true)
        setToBeRemoved(uuid)
    }

    const handleHideConfirmation = async () => {
        setIsConfirmationDialogVisible(false)
    }

    const handleAddEWalletOperation = async (
        provider: string,
        phone_number: string,
        label: string,
        revalidate: boolean = false,
        ewallet_uuid?: string,
    ) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.addedSuccessfully'));
        try {
            let response
            if (revalidate && ewallet_uuid) {
                response = await validateEWallet(ewallet_uuid)
            } else {
                response = await addEWallet({
                    provider: provider,
                    phone_number: phone_number,
                    label: label,
                })
            }
            if (response.status === 'VALIDATED') {
                setSuccessOpen(true);
                setTimeout(async () => {
                    onBackClick(response.uuid)
                }, 2000)
            } else if (response.status === 'INVALID') {
                setFailedModalVisible(true);
            } else if (response.status === 'VALIDATION_REQUESTED') {
                if (ewallet_uuid && revalidate) {
                    setVerificationModalVisible(true);
                } else {
                    await handleAddEWalletOperation(provider, phone_number, label, true, response.uuid)
                }
            }
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                // Check for specific e-wallet restriction error
                if (error.message?.includes('management is disabled')) {
                    setErrorMessage(t('errors.managementRestrictions.ewalletDisabled'))
                    return
                }

                // Check if translation exists for this error code, otherwise use generic message
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                // If the translation key doesn't exist, t() will return the key itself
                const errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')

                setErrorMessage(errorMessage)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleEditEWalletOperation = async (
        provider: string,
        phone_number: string,
        label: string,
        revalidate: boolean = false,
        ewallet_uuid: string,
    ) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.updatedSuccessfully'));
        try {
            let response
            if (revalidate && ewallet_uuid) {
                response = await validateEWallet(ewallet_uuid)
            } else {
                response = await editEWallet({
                    uuid: ewallet_uuid,
                    provider: provider,
                    phone_number: phone_number,
                    label: label,
                })
            }
            if (response.status === 'VALIDATED') {
                setSuccessOpen(true);
                setTimeout(async () => {
                    onBackClick()
                }, 2000)
            } else if (response.status === 'INVALID') {
                setFailedModalVisible(true);
            } else if (response.status === 'VALIDATION_REQUESTED') {
                if (ewallet_uuid && revalidate) {
                    setVerificationModalVisible(true);
                } else {
                    await handleEditEWalletOperation(provider, phone_number, label, true, response.uuid)
                }
            }
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                // Check for specific e-wallet restriction error
                if (error.message?.includes('management is disabled')) {
                    setErrorMessage(t('errors.managementRestrictions.ewalletDisabled'))
                    return
                }

                // Check if translation exists for this error code, otherwise use generic message
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                // If the translation key doesn't exist, t() will return the key itself
                const errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')

                setErrorMessage(errorMessage)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleRemoveEWalletOperation = async (ewallet_uuid: string) => {
        setIsLocalLoading(true)
        setSuccessMessage(t('authentication.successMessages.removedSuccessfully'));
        try {
            await removeEWallet(ewallet_uuid)
            setSuccessOpen(true);

            setTimeout(async () => {
                onBackClick()
            }, 2000)
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                // Check if translation exists for this error code, otherwise use generic message
                const translationKey = `errors.${error.errorCode}.description`
                const translatedMessage = t(translationKey)

                // If the translation key doesn't exist, t() will return the key itself
                const errorMessage = translatedMessage !== translationKey
                    ? translatedMessage
                    : t('errors.genericServerDescription')

                setErrorMessage(errorMessage)
                return
            }
            setErrorMessage(t('errors.genericServerDescription'));
            setIsError(true)
        } finally {
            setIsLocalLoading(false)
        }
    };

    const handleSave = async (ewallet: EWallet) => {
        // If we came from a redeem page, navigate back to it
        if (source === 'redeem-e-wallet') {
            await handleAddEWalletOperation(ewallet.provider, ewallet.phone_number, ewallet.label)
        } else {
            // Otherwise, maintain original behavior
            if (selectedEWallet) {
                await handleEditEWalletOperation(ewallet.provider, ewallet.phone_number, ewallet.label, false, ewallet.uuid)
            } else {
                await handleAddEWalletOperation(ewallet.provider, ewallet.phone_number, ewallet.label)
            }
        }
    };

    const handleRemove = async (ewallet_uuid: string) => {
        await handleRemoveEWalletOperation(ewallet_uuid)
    };

    return (
        <Container>
            <Header title={t('bankAndAddress.ewallet.header')} showBackButton onBackClick={async () => await onBackClick()} />
            {isLoadingEWallets && <LoadingModal visible={isLoadingEWallets} />}
            {isLocalLoading && <LoadingModal visible={isLocalLoading} />}
            <BodyContainer>
                {mode === 'view' && <ViewModeContainer>
                    <SectionLabel>{t('bankAndAddress.ewallet.sectionLabel')}</SectionLabel>
                    {ewallets && !ewallets.length &&
                        <EmptyListContainer>
                            <EmptyList>
                                <EmptyListText>
                                    {t('bankAndAddress.ewallet.emptyText')}
                                </EmptyListText>
                                {canManageEwalletAccounts() && <AddButton onClick={handleAddNewClick} label={t('bankAndAddress.ewallet.addNew')} $backgroundColor={PALE_BLUE} />}
                            </EmptyList>
                        </EmptyListContainer>}
                    {
                        ewallets &&
                        ewallets.length > 0 &&
                        !isLoadingEWallets &&
                        supportedEWallets && (
                            ewallets.map((ewallet) => (
                                <EWalletItemContainer key={ewallet.uuid}>
                                    <AccountListItem
                                        oneLine
                                        name={ewallet.label}
                                        accountNumber={ewallet.phone_number}
                                        iconUrl={ewallet.iconUrl}
                                        provider={ewallet.provider}
                                        isValid={ewallet.status === "VALIDATED"}
                                        tag={ewallet.status === "VALIDATED" && canManageEwalletAccounts() ? "edit" : "none"}
                                        onClick={() => handleEWalletClick(ewallet)}
                                    />
                                </EWalletItemContainer>
                            ))
                        )}
                    {ewallets && ewallets.length !== 0 && canManageEwalletAccounts() && <BottomButtonContainer>
                        <AddButton onClick={handleAddNewClick} label={t('bankAndAddress.ewallet.addNew')} $backgroundColor={PALE_BLUE} />
                    </BottomButtonContainer>}
                </ViewModeContainer>}
                {mode === 'edit' &&
                    <EWalletForm
                        supportedEWallets={supportedEWallets}
                        onRemove={handlePromptConfirmation}
                        onSave={handleSave}
                        initialName={selectedEWallet?.label}
                        initialAccountNumber={selectedEWallet?.phone_number}
                        initialProvider={selectedEWallet?.provider}
                        uuid={selectedEWallet?.uuid}
                        errorText={errorMessage}
                        mode="edit"
                    />
                }
                {mode === 'add' &&
                    <EWalletForm
                        supportedEWallets={supportedEWallets}
                        onRemove={handlePromptConfirmation}
                        onSave={handleSave}
                        initialName={selectedEWallet?.label}
                        initialAccountNumber={selectedEWallet?.phone_number}
                        initialProvider={selectedEWallet?.provider}
                        uuid={selectedEWallet?.uuid}
                        errorText={errorMessage}
                        mode="add"
                    />
                }
            </BodyContainer>
            {successOpen && <SuccessModal
                visible={successOpen}
                message={successMessage}
                onHide={() => setSuccessOpen(false)}
            />}
            {verificationModalVisible && <ModalWithIcon
                title={t('errors.verification.inProgress.ewallet.title')}
                description={t('errors.verification.inProgress.ewallet.description')}
                onPositive={async () => {
                    setVerificationModalVisible(false)
                    onBackClick()
                }}
                positiveText={t('errors.verification.inProgress.positiveText')}
                visible={verificationModalVisible}
                type="void"
            />}
            {failedModalVisible && <ModalWithIcon
                title={t('errors.verification.failed.ewallet.title')}
                description={t('errors.verification.failed.ewallet.description')}
                onPositive={async () => {
                    setFailedModalVisible(false)
                    onBackClick()
                }}
                positiveText={t('errors.verification.failed.positiveText')}
                visible={failedModalVisible}
                type="failed"
            />}
            {isError && <Snackbar
                width='90%'
                duration={8000}
                message={errorMessage}
                isVisible={isError}
                onClose={() => setErrorMessage('')}
            />}
            {isConfirmationDialogVisible && <DialogConfirmationBox
                isOpen={isConfirmationDialogVisible}
                title={t('bankAndAddress.ewallet.removeConfirmation.title')}
                description={t('bankAndAddress.ewallet.removeConfirmation.description')}
                positiveButtonText={t('bankAndAddress.ewallet.removeConfirmation.positiveButtonText')}
                negativeButtonText={t('bankAndAddress.ewallet.removeConfirmation.negativeButtonText')}
                onPositiveClick={handleHideConfirmation}
                onNegativeClick={async () => {
                    await handleHideConfirmation()
                    await handleRemove(toBeRemoved)
                }}
                onOverlayClick={handleHideConfirmation}
            />}
        </Container>
    )
}

export default ManageEWallet;